/* eslint-disable no-param-reassign */
import Sentry from '@/Utils/Sentry';
import datazoom from '@datazoom/collector_brightcove_mt_om';
import { DATAZOOM_CONFIG_ID_BRIGHTCOVE, PLAYER_TYPES } from '../Constant';
import { transformEMEPlayback } from '../DRM';
import { initializeMediaTailorSession } from './DataZoomMediaTailorConfig';
import {
  PlaybackAsset,
  PlayerControllerFactory,
  SsaiAdPodProps,
  SsaiAdBreakProps,
} from './PlayerTypes';

const LIVE_TOLERANCE = 30;
/**
 * Factory for generating a player controller with the tech spec of Shaka Player
 * @param player
 * @param videoElement
 */
const BrightCoveControllerFactory: PlayerControllerFactory = (
  player,
  videoElement,
  accessToken,
  setIsAdPlaying,
) => {
  const playerController = {
    videoEl: videoElement,

    focus: () => player?.focus?.(),
    playsInline: () => player?.playsinline?.(),
    readyState: () => player?.readyState?.(),

    loadMeta: async (playbackAsset: PlaybackAsset) => {
      const playSource = transformEMEPlayback(playbackAsset, accessToken);

      // transformVideoResponse is needed to play properly in safari
      const transformedPlaySource = player.catalog.transformVideoResponse(playSource);

      try {
        if (playbackAsset?.adTrackingUri) {
          initializeMediaTailorSession({
            datazoom,
            playbackAsset,
            player,
            setIsAdPlaying,
            playerType: PLAYER_TYPES.BRIGHTCOVE,
            configurationId: DATAZOOM_CONFIG_ID_BRIGHTCOVE,
          });
        }
        player.catalog.load(transformedPlaySource);
      } catch (error) {
        Sentry.captureMessage(
          `Brightcove player load failed. Reason: ${error}. PlaybackAsset URI: ${
            playbackAsset?.streamUri || 'N/A'
          }, PlaySource: ${JSON.stringify(playSource)}`,
        );
        throw error;
      }
    },

    subTitle: (selectedTrack?: any) => {
      player.ready(() => {
        const tracks = player.textTracks()?.tracks_;
        if (!Array.isArray(tracks)) return;

        tracks.forEach((track: any) => {
          track.mode = 'disabled';
          if (
            (track.kind === 'subtitles' || track.kind === 'captions') &&
            track.language === selectedTrack?.language
          ) {
            track.mode = 'showing';
          }
        });
      });
    },

    dispose: () => player?.dispose?.(),

    volume: (volume?: number): number | undefined => {
      if (!player || typeof player.volume !== 'function') return undefined;
      return volume !== undefined ? player.volume(volume) : player.volume();
    },

    currentTime: (time?: number): number | null => {
      if (!player || !player.el_ || typeof player.currentTime !== 'function') {
        return null;
      }
      return time !== undefined ? player.currentTime(time) : player.currentTime();
    },

    videoHeight: (): number | null => (player?.videoHeight ? player.videoHeight() : null),

    autoplay: (autoplay?: boolean): boolean | undefined => {
      if (!player || typeof player.autoplay !== 'function') return undefined;
      return autoplay !== undefined ? player.autoplay(autoplay) : player.autoplay();
    },

    muted: (muted?: boolean): boolean | undefined => {
      if (!player || typeof player.muted !== 'function') return undefined;
      return muted !== undefined ? player.muted(muted) : player.muted();
    },

    play: async () => player?.play?.(),
    pause: () => player?.pause?.(),
    duration: (): number | null => (player?.duration ? player.duration() : null),
    buffered: () => player?.buffered?.(),

    seekToLiveEdge: () => {
      player?.liveTracker?.seekToLiveEdge?.();
    },

    getLiveCurrentTime: (): number => {
      const liveTracker = player?.liveTracker;
      if (
        !liveTracker ||
        !liveTracker.player_ ||
        typeof liveTracker.liveCurrentTime !== 'function'
      ) {
        return -1;
      }
      return liveTracker.liveCurrentTime();
    },

    getLiveWindowDuration: (): number => {
      const liveTracker = player?.liveTracker;
      if (
        !liveTracker ||
        typeof liveTracker.seekableEnd !== 'function' ||
        typeof liveTracker.seekableStart !== 'function'
      ) {
        return -1;
      }

      return Math.floor(liveTracker.seekableEnd() - liveTracker.seekableStart());
    },

    getLiveStartOffset: (): number => {
      const liveTracker = player?.liveTracker;
      if (
        !liveTracker ||
        typeof liveTracker.seekableStart !== 'function' ||
        typeof liveTracker.pastSeekEnd !== 'function'
      ) {
        return -1;
      }

      return liveTracker.seekableStart() + liveTracker.pastSeekEnd();
    },

    isAtLiveEdge: (tolerance?: number): boolean => {
      const liveCurrentTime = playerController.getLiveCurrentTime?.();
      const currentTime = playerController.currentTime?.();

      if (typeof liveCurrentTime !== 'number' || typeof currentTime !== 'number') {
        return false;
      }

      return Math.abs(liveCurrentTime - currentTime) <= (tolerance || LIVE_TOLERANCE);
    },

    hasVmap: (): boolean => {
      if (!player?.mediainfo?.sources || player.mediainfo.sources.length === 0) {
        return false;
      }
      return !!player.mediainfo.sources[0].vmap;
    },

    getAdPods: (startTime: number): SsaiAdPodProps[] => {
      if (!player?.ssai().timeline_) {
        return [];
      }

      const linearAdRolls = player.ssai().timeline_.linearAdRolls_;
      const adPods = linearAdRolls?.map(adPod => {
        const adPodType = `${adPod.type_}roll`;
        const adBreaks: SsaiAdBreakProps[] = adPod.linearAds_.map((ad, index) => ({
          adTitle: ad.adTitle_,
          adType: `${adPodType}_${adPod.podIndex_}_${index}`,
          adId: ad.id_,
          adSystem: ad.adSystem_,
          startTime: ad.absoluteStartTime_,
          endTime: ad.absoluteEndTime_,
          duration: ad.duration_,
          index,
        }));
        return {
          podId: `${adPodType}_${adPod.podIndex_}`,
          adBreaks,
          type: adPodType,
          startTime: adPod.startTimeOffset_,
          endTime: adPod.startTimeOffset_ + adPod.duration_,
          duration: adPod.duration_,
          index: adPod.podIndex_,
          isWatched: adPod.type_ === 'pre' && startTime > 0,
        };
      });
      return adPods || [];
    },

    // Seek to a content-relative time, automatically accounting for ad durations.
    seekInContentTime: (requestedTime: number, preventAdSkips = false) => {
      if (!player || !player.el_ || typeof player.ssai().seekInContentTime !== 'function') {
        return;
      }
      player.ssai().seekInContentTime(requestedTime, preventAdSkips);
    },

    // Seek to a specific absolute stream time. When preventAdSkips is true, ad enforcement prevents skipping over ads.
    seekInAbsoluteTime: (requestedTime: number, preventAdSkips = false) => {
      if (!player || !player.el_ || typeof player.ssai().seekInAbsoluteTime !== 'function') {
        return;
      }
      player.ssai().seekInAbsoluteTime(requestedTime, preventAdSkips);
    },

    absoluteTime: (): number => {
      if (
        !player ||
        !player.el_ ||
        typeof player.ssai().currentTimelineState !== 'function' ||
        !player?.ssai().timeline_
      ) {
        return player.currentTime();
      }
      return player.ssai().currentTimelineState().absoluteTime;
    },

    isSsaiAdPlaying: (): boolean => {
      if (
        !player ||
        !player.el_ ||
        typeof player.ssai().currentTimelineState !== 'function' ||
        !player?.ssai().timeline_
      ) {
        return false;
      }
      return !!player.ssai().currentTimelineState().linearAd;
    },

    isSsaiPrerollPlaying: (): boolean => {
      if (
        !player ||
        !player.el_ ||
        typeof player.ssai().currentTimelineState !== 'function' ||
        !player?.ssai().timeline_
      ) {
        return false;
      }
      return player.ssai().currentTimelineState().linearAdRoll?.isPreRoll();
    },

    isSsaiMidrollPlaying: (): boolean => {
      if (
        !player ||
        !player.el_ ||
        typeof player.ssai().currentTimelineState !== 'function' ||
        !player?.ssai().timeline_
      ) {
        return false;
      }
      return player.ssai().currentTimelineState().linearAdRoll?.isMidRoll();
    },
  };

  return playerController;
};

export default BrightCoveControllerFactory;
